/*
 * Shared API transport: RSA-OAEP-SHA-256 + AES-256-GCM.
 *
 * No symmetric key is embedded in this file. The server public key is safe to
 * expose, while its matching private key must remain only on the API server.
 */
var axios = require('axios')

export const secureApiConfig = {
  // Deploy these two public values to avoid a key-fetch request. They are not
  // secrets: only the matching RSA private key stays on the API server.
  kid: '202609',
  publicKeyPem: "-----BEGIN PUBLIC KEY-----\n" +
    "MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEA3ofAMQXa22Fyy7/H4aOQ\n" +
    "0msbg/m6pg8Latnt3IXKLpFJsHcSKz+vaP5DcOh1Qkb6QhA2ZbYS4SEbzkHTB5HP\n" +
    "e42TpRWEVnfzNwly5wcw3NnWg5HEgqlN3TsxrDm3Wo6rSWflhNM9MvseL2380MMS\n" +
    "YajJ2hnZ33mnjAC6obzuDenhe8M1xNHyPJ7FlzjBDRF1uTCWoGlKpgwh8I4hJ6bv\n" +
    "JQibCDjiMCNPhMhId2uVBc58QWbPy+xjb2XB09Sb3Y6rehB0T3LPxc+qCOjiFQlH\n" +
    "Za30TuY/yE2dY12njAIa037PHVorCtxjB8LgZzf9WP+ohGSjjUOV6B2fR6igeT41\n" +
    "vwIDAQAB\n" +
    "-----END PUBLIC KEY-----",
}

var cachedPublicKey = null
var cachedKid = ''

function getWebCrypto () {
  var webCrypto = window.crypto || window.msCrypto
  if (!webCrypto || !webCrypto.subtle || !webCrypto.getRandomValues) {
    throw new Error('Secure login is not supported by this browser')
  }
  return webCrypto
}

function bytesToBase64 (bytes) {
  var binary = ''
  var view = new Uint8Array(bytes)
  for (var index = 0; index < view.length; index++) {
    binary += String.fromCharCode(view[index])
  }
  return window.btoa(binary)
}

function base64ToBytes (value) {
  var binary = window.atob(value)
  var bytes = new Uint8Array(binary.length)
  for (var index = 0; index < binary.length; index++) {
    bytes[index] = binary.charCodeAt(index)
  }
  return bytes.buffer
}

function textToBytes (value) {
  return new TextEncoder().encode(value)
}

function bytesToText (value) {
  return new TextDecoder().decode(value)
}

function randomBytes (length) {
  var bytes = new Uint8Array(length)
  getWebCrypto().getRandomValues(bytes)
  return bytes
}

function buildAad (direction, method, path, timestamp, nonce) {
  return textToBytes([direction, method.toUpperCase(), path, timestamp, nonce].join('\n'))
}

function pemToSpki (pem) {
  var base64 = pem
    .replace(/-----BEGIN PUBLIC KEY-----/g, '')
    .replace(/-----END PUBLIC KEY-----/g, '')
    .replace(/\s/g, '')

  return base64ToBytes(base64)
}

function importServerPublicKey (kid, publicKeyPem) {
  return getWebCrypto().subtle.importKey(
    'spki',
    pemToSpki(publicKeyPem),
    {name: 'RSA-OAEP', hash: {name: 'SHA-256'}},
    false,
    ['encrypt']
  ).then(function (key) {
    cachedPublicKey = key
    cachedKid = kid
    return {key: key, kid: kid}
  })
}

function getServerPublicKey () {
  if (cachedPublicKey) {
    return Promise.resolve({key: cachedPublicKey, kid: cachedKid})
  }

  if (secureApiConfig.kid && secureApiConfig.publicKeyPem) {
    return importServerPublicKey(
      secureApiConfig.kid,
      secureApiConfig.publicKeyPem
    )
  }

  return Promise.reject(new Error('No secure API public key is configured'))
}

function encryptRequest (method, path, payload) {
  var webCrypto = getWebCrypto()
  var timestamp = Math.floor(Date.now() / 1000).toString()
  var nonce = bytesToBase64(randomBytes(16))
  var iv = randomBytes(12)
  var plainText = JSON.stringify({
    data: payload
  })
  var requestAad = buildAad('request', method, path, timestamp, nonce)

  return getServerPublicKey().then(function (server) {
    return webCrypto.subtle.generateKey(
      {name: 'AES-GCM', length: 256},
      true,
      ['encrypt', 'decrypt']
    ).then(function (aesKey) {
      return Promise.all([
        webCrypto.subtle.exportKey('raw', aesKey),
        webCrypto.subtle.encrypt(
          {name: 'AES-GCM', iv: iv, additionalData: requestAad, tagLength: 128},
          aesKey,
          textToBytes(plainText)
        )
      ]).then(function (result) {
        return webCrypto.subtle.encrypt({name: 'RSA-OAEP'}, server.key, result[0])
          .then(function (encryptedKey) {
            return {
              aesKey: aesKey,
              timestamp: timestamp,
              nonce: nonce,
              body: {
                v: 1,
                kid: server.kid,
                timestamp: timestamp,
                nonce: nonce,
                encrypted_key: bytesToBase64(encryptedKey),
                iv: bytesToBase64(iv),
                ciphertext: bytesToBase64(result[1])
              }
            }
          })
      })
    })
  })
}

function decryptResponse (method, path, request, responseBody) {
  var body = typeof responseBody === 'string' ? JSON.parse(responseBody) : responseBody
  if (!body || body.v !== 1 || !body.iv || !body.ciphertext) {
    throw new Error('Invalid secure API response')
  }

  var responseAad = buildAad('response', method, path, request.timestamp, request.nonce)
  return getWebCrypto().subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: new Uint8Array(base64ToBytes(body.iv)),
      additionalData: responseAad,
      tagLength: 128
    },
    request.aesKey,
    base64ToBytes(body.ciphertext)
  ).then(function (plainText) {
    return JSON.parse(bytesToText(plainText))
  })
}

function secureApiAxios (method, path, payload, success, failure) {
  return encryptRequest(method, path, payload).then(function (request) {
    return axios({
      method: method,
      url: path,
      data: request.body,
      baseURL: process.env.BASE_URL || '/',
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
        language: localStorage.getItem('lang') || 'bur'
      }
    }).then(function (response) {
      return decryptResponse(method, path, request, response.data)
    })
  }).then(function (result) {
    if (success) {
      success(result)
    }
    return result
  }).catch(function (error) {
    if (failure) {
      failure(error)
    }
    return null
  })
}

// Same callback signature as this.$api.post: replace only "$api" with
// "$secureApi" for endpoints migrated to the version-1 envelope.
export default {
  post: function (url, params, success, failure) {
    return secureApiAxios('POST', url, params, success, failure)
  }
}



// WEBPACK FOOTER //
// ./src/api/secure-api.js